/**
 * Clock formatting for durations and playhead positions, shared by web and
 * mobile so the same recording reads the same on both.
 *
 * The hour field appears only once the duration reaches an hour. Without that
 * rule a long recording renders as a minute count past sixty.
 */

export interface ClockOptions {
  /** Zero-pad the leading minute field below an hour. Defaults to true. */
  padMinutes?: boolean;
}

/** Seconds as `M:SS`, `MM:SS` or `H:MM:SS`. Negative and NaN input read as 0. */
export function formatClock(totalSeconds: number, options: ClockOptions = {}): string {
  const padMinutes = options.padMinutes ?? true;
  const safe = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${ss}`;
  return `${padMinutes ? String(minutes).padStart(2, "0") : minutes}:${ss}`;
}

/** Milliseconds as a clock string. */
export function formatClockMs(ms: number, options: ClockOptions = {}): string {
  return formatClock(Number.isFinite(ms) ? ms / 1000 : 0, options);
}
